import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { MetricTimeSeriesBarGraph } from '../../../components/metricTimeSeriesBarGraph/MetricTimeSeriesBarGraph';
import AgentMonthlyMetric from '../agentMonthlyMetric/AgentMonthlyMetric';
import AgentPieChart from '../agentPieChart/AgentPieChart';
import GradientDefs, { MetricColors } from '../../../components/metricMonthlySummaryCard/GradientDefs';
import { getExceedsTarget } from '../../../utils/metricAttainment';

interface PropTypes {
    empNum?: number;
    inputyear?: number;
    inputmonth?: number;
    scorecardVersion?: number;
    monthlyMetricsData: any;
    monthlyMetricHistory: any;
    dailyMetricsData: any;
}

const AgentScoreCardDashboard = memo(({
    empNum,
    inputyear,
    inputmonth,
    scorecardVersion,
    monthlyMetricsData,
    monthlyMetricHistory,
    dailyMetricsData,
}: PropTypes) => {
    const [selectedMetricName, setSelectedMetricName] = useState<string>('');
    const [selectedMetricColorIndex, setSelectedMetricColorIndex] = useState(0);

    const selectedMetricBgColor = useMemo(
        () => (MetricColors[selectedMetricColorIndex] ?? MetricColors[0]).bgColor,
        [selectedMetricColorIndex]
    );

    useEffect(() => {
        if (monthlyMetricHistory?.data.length > 0) {
            setSelectedMetricName(monthlyMetricHistory.data[0].label);
            setSelectedMetricColorIndex(0);
        }
    }, [monthlyMetricHistory]);

    const filteredDailyData = useMemo(
        () => dailyMetricsData?.data?.filter((element: any) => element.label === selectedMetricName) ?? [],
        [dailyMetricsData, selectedMetricName]
    );

    const filteredHistoryData = useCallback(
        (label: string) =>
            monthlyMetricHistory?.data?.filter((element: any) => element.label === label) ?? [],
        [monthlyMetricHistory]
    );

    return (
        <>
            <GradientDefs />
            <div className="card shadow bg-body rounded mt-3 border-1">
                <div className="d-flex flex-nowrap" style={{ backgroundColor: selectedMetricBgColor, alignItems: 'stretch' }}>
                    <div className="flex-grow-1 flex-basis-0" style={{ height: '100%' }}>
                        <AgentMonthlyMetric
                            monthlyMetricsData={monthlyMetricsData}
                            setMetricName={setSelectedMetricName}
                            setMetricColorIndex={setSelectedMetricColorIndex}
                        />
                    </div>
                    <div className="flex-grow-2 flex-basis-0">
                        <AgentPieChart
                            empNum={empNum}
                            inputmonth={inputmonth}
                            inputyear={inputyear}
                            scorecardVersion={scorecardVersion}
                        />
                    </div>
                </div>
                {selectedMetricName && (
                    <div>
                        {filteredDailyData.map((filteredElement: any, index: number) => (
                            <MetricTimeSeriesBarGraph
                                key={index}
                                metricName={filteredElement.label}
                                metricId={filteredElement.metric_id}
                                scorecardVersionId={scorecardVersion ?? 0}
                                employeeNumber={empNum?.toString() ?? ''}
                                target={filteredElement.target}
                                metricTypeName={filteredElement.metric_type_name}
                                dailyData={filteredElement.dailyMetricSummaryList}
                                metricDirectionId={filteredElement.metric_direction_id}
                                calculatedDate={filteredElement.calculate_date}
                                metricHistoryData={filteredHistoryData(selectedMetricName)}
                                fromMetricDashboard={false}
                                metricIndex={selectedMetricColorIndex}
                                exceedsTarget={getExceedsTarget(filteredElement.targets)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
});

AgentScoreCardDashboard.displayName = 'AgentScoreCardDashboard';

export default AgentScoreCardDashboard;
